<template>
    <div v-if="loaded">
        <div class="border border-gray-dark">
            <div
                class="mx-4 border-b border-gray-dark flex justify-between items-end"
            >
                <h2
                    class="md:w-1/2 font-bold text-xl px-4 py-4 flex items-baseline justify-between"
                >
                    {{ magazine.name }}
                </h2>
                <div
                    class="md:w-1/2 items-center space-x-1 flex justify-end px-2 py-4"
                >
                    <button
                        class="bg-primary text-white text-sm px-2 md:py-1 py-2 rounded"
                        @click.prevent="editMagazine"
                    >
                        <font-awesome-icon
                            :icon="['fa', 'edit']"
                        ></font-awesome-icon>
                        <span class="hidden md:inline">Edit</span>
                    </button>
                    <button
                        class="bg-destructive text-white text-sm px-2 md:py-1 py-2 rounded"
                        @click.prevent="deleteMagazine"
                    >
                        <font-awesome-icon
                            :icon="['fa', 'trash-alt']"
                        ></font-awesome-icon>
                        <span class="hidden md:inline">Delete</span>
                    </button>
                </div>
            </div>
            <template v-if="deliveries && deliveries.length > 0">
                <nav class="my-2 px-2 inline-block md:hidden">
                    <div class="relative">
                        <button
                            class="mx-1 p-2 bg-primary-lighter border-gray-dark border rounded text-primary"
                            @click.prevent="toggleDeliveries"
                        >
                            <font-awesome-icon
                                :icon="['fas', 'book-open']"
                            ></font-awesome-icon>
                            <span
                                v-if="isActiveDelivery(delivery.id)"
                                v-for="delivery in deliveries"
                                :key="delivery.id"
                                >{{ delivery.delivered_at }}</span
                            >
                            <font-awesome-icon
                                :icon="['fas', 'caret-down']"
                                class="ml-2"
                            ></font-awesome-icon>
                        </button>
                        <button
                            v-if="isDeliveriesOpen"
                            @click.prevent="toggleDeliveries"
                            tabindex="-1"
                            class="fixed inset-0 h-full w-full cursor-default z-20"
                        ></button>
                        <div
                            class="block absolute left-0 top-0 border border-gray-dark py-2 w-full bg-white rounded shadow-xl z-30"
                            :class="isDeliveriesOpen ? '' : 'hidden'"
                        >
                            <ul class="my-1">
                                <li
                                    class="my-1 p-4 hover:bg-primary hover:text-white rounded cursor-pointer"
                                    :class="
                                        isActiveDelivery(delivery.id)
                                            ? 'bg-primary text-white font-bold'
                                            : ''
                                    "
                                    @click="setActiveDelivery(delivery.id)"
                                    v-for="delivery in deliveries"
                                    :key="delivery.id"
                                >
                                    <font-awesome-icon
                                        :icon="['fas', 'book-open']"
                                        class="text-primary-lighter mr-2"
                                    ></font-awesome-icon>
                                    <span class="text-sm">{{
                                        delivery.delivered_at
                                    }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </nav>
                <div class="my-2 px-2 flex items-start">
                    <aside
                        class="hidden md:block md:w-1/4 sticky top-0 h-screen px-1 bg-gray-light border border-gray-dark rounded"
                    >
                        <ul class="my-1">
                            <li
                                class="my-1 p-4 hover:bg-primary hover:text-white rounded cursor-pointer"
                                :class="
                                    isActiveDelivery(delivery.id)
                                        ? 'bg-primary text-white font-bold'
                                        : ''
                                "
                                @click="setActiveDelivery(delivery.id)"
                                v-for="(delivery, index) in deliveries"
                                :key="delivery.id"
                            >
                                <font-awesome-icon
                                    :icon="['fas', 'book-open']"
                                    class="text-primary-lighter mr-2"
                                ></font-awesome-icon>
                                <span class="text-sm">{{
                                    delivery.delivered_at.substr(4)
                                }}</span>
                            </li>
                        </ul>
                    </aside>
                    <div
                        class="md:w-3/4 px-1 rounded mt-2 text-sm"
                        v-if="
                            currentDelivery &&
                            currentDelivery.messages.length > 0
                        "
                    >
                        <div
                            v-for="(
                                delivery, index
                            ) in currentDelivery.messages"
                            class="px-4 py-2 border-b border-gray-dark relative bg-white"
                            :key="delivery.id"
                            :class="
                                !expanded[index] ? 'hover:bg-gray-light' : ''
                            "
                        >
                            <div
                                class="cursor-pointer"
                                :class="
                                    expanded[index]
                                        ? 'bg-white sticky top-0 pb-2'
                                        : 'pb-1'
                                "
                                @click.prevent="
                                    handleCollapse(index, delivery.id)
                                "
                            >
                                <div
                                    class="flex items-center justify-between mb-1"
                                >
                                    <p class="font-bold truncate">
                                        <span class="hidden md:inline">{{
                                            delivery.from_name
                                        }}</span>
                                        <span
                                            class="md:text-gray-darker truncate md:font-extralight"
                                            >{{ delivery.from_address }}</span
                                        >
                                    </p>
                                    <span class="text-gray-darker mt-2">{{
                                        delivery.received_at
                                    }}</span>
                                </div>
                                <div>
                                    <p>
                                        {{ delivery.subject }}
                                    </p>
                                </div>
                            </div>
                            <section class="ml-1 pb-1">
                                <section
                                    v-if="expanded[index]"
                                    class="py-1 mb-6"
                                >
                                    <iframe
                                        :srcdoc="message"
                                        class="w-full h-screen"
                                        title="Magazine message"
                                        onload="resizeIframe(this)"
                                    ></iframe>
                                </section>
                            </section>
                        </div>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="text-center">
                    <p class="mx-2 xl:mx-32 my-16 font-bold text-primary">
                        Now, configure what emails must be a part of this
                        magazine via
                        <a href="/reviewer" class="underline">Daily Digest</a>.
                    </p>
                </div>
            </template>
        </div>
    </div>
    <div v-else>
        <loading />
    </div>
</template>
<script>
import ConfirmationModal from "./ConfirmationModal.vue";
import editMagazineModal from "./EditMagazineModal.vue";

window.resizeIframe = function (obj) {
    obj.style.height =
        obj.contentWindow.document.documentElement.scrollHeight + "px";
};
export default {
    name: "show-magazine",
    props: {
        id: {
            required: true,
        },
    },
    data() {
        return {
            magazine: null,
            deliveries: null,
            currentDelivery: null,
            loaded: false,
            activeDelivery: "",
            messages: [],
            isDeliveriesOpen: false,
            expanded: [],
            message: "",
        };
    },
    methods: {
        handleCollapse(index, id) {
            let status = _.cloneDeep(this.expanded[index]);
            this.$set(
                this,
                "expanded",
                Array(this.currentDelivery.messages.length).fill(false)
            );
            this.$set(this.expanded, index, !status);
            if (!status) this.getMessage(id);
        },
        toggleDeliveries() {
            this.isDeliveriesOpen = !this.isDeliveriesOpen;
        },
        setActiveDelivery(delivery) {
            this.activeDelivery = delivery;

            this.getDelivery(delivery);

            this.isDeliveriesOpen = false;
        },
        isActiveDelivery(delivery) {
            return this.activeDelivery == delivery;
        },
        getDelivery(magazineDelivery) {
            axios
                .get(
                    `/api/magazines/${this.id}/deliveries/${magazineDelivery}?messages`
                )
                .then((response) => {
                    this.currentDelivery = response.data.data;
                    this.expanded = Array(
                        this.currentDelivery.messages.length
                    ).fill(false);
                    this.loaded = true;
                })
                .catch((err) => {
                    console.log(err);
                });
        },
        getMessage(messageId) {
            this.message = "";
            axios
                .get(`/api/magazines/${this.id}/messages/${messageId}?full`)
                .then((response) => {
                    let fullMessage = response.data.data;
                    this.message = fullMessage.body_html;
                    this.loaded = true;
                })
                .catch((err) => {
                    console.log(err);
                });
        },
        getMagazine() {
            axios
                .get(`/api/magazines/${this.id}`)
                .then((response) => (this.magazine = response.data.data))
                .catch((err) => console.log(err));
        },
        getDeliveries() {
            let query = window.location.search.substring(1);
            let params = new URLSearchParams(query);
            const deliveryId = params.get("delivery");
            this.loaded = false;
            axios
                .get(`/api/magazines/${this.id}/deliveries`)
                .then((response) => {
                    this.deliveries = response.data.data;
                    this.loaded = true;
                    if (this.deliveries.length > 0) {
                        let magazineDelivery = deliveryId
                            ? deliveryId
                            : this.deliveries[0].id;
                        this.setActiveDelivery(magazineDelivery);
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        },
        deleteMagazine() {
            Modal.show(ConfirmationModal, {
                propsData: {
                    header: "Delete magazine",
                    message:
                        "Are you sure you want to delete this magazine? All deliveries and emails will be lost. This action is permanent and cannot be undone.",
                },
            })
                .then(() => {
                    axios
                        .delete(`/api/magazines/` + this.id)
                        .then((response) => {
                            Event.$emit("toast", {
                                text: "Magazine deleted successfully.",
                            });
                            window.location = "/magazines";
                        })
                        .catch((err) => {
                            if (err.response.status === 403 && err.response?.data?.message) {
                                Event.$emit('toast', { text: err.response.data?.message, type: 'danger' });
                            }
                            console.error(err);
                        });
                })
                .catch((err) => {
                    console.error(err);
                });
        },
        editMagazine() {
            const magazineId = this.id;
            Modal.show(editMagazineModal, {
                propsData: {
                    magazineId,
                },
            })
                .then(() => {
                    Event.$emit("toast", {
                        text: "Magazine updated successfully.",
                    });
                    this.getMagazine();
                    this.getDeliveries();
                })
                .catch((err) => {
                    console.error(err);
                });
        },
    },
    mounted() {
        this.getMagazine();
        this.getDeliveries();
    },
};
</script>
<style lang="scss"></style>
